import { FunctionalComponent } from 'preact'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { currentPathname } from '@/utils/currentPathname'
import { leadsApi, type CampaignLeadAnalyticsResponseDto, type LeadSignalTimelineDto, type CompanySignalDto } from '@/api/leads'
import AudienceViewToggle from '@/components/common/AudienceViewToggle'
import { DEFAULT_AUDIENCE_VIEW, emptyStateMessage, filterCompaniesByAudience, filterLeadsByAudience, type AudienceView } from '@/utils/leadAudienceFilter'
import { SignalBars } from '@/components/common/SignalBars'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'
import { useEnrichmentEvent } from '@/hooks/useEnrichmentEvent'
import { PersonAvatar } from '@/components/common/Avatar'
import { CompanyNameCell } from '@/components/companies/CompanyNameCell'
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell'
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell'
import { getWebTypeMeta, type WebTypeKey } from '@/constants/webTypes'
import { timeFromNowVerbose } from '@/utils/time'
import { RefreshCcw } from 'lucide-preact'
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting'
import { getLeadDisplayName, shouldShowEmailLine } from '@/utils/leadFormatting'
import { LoadingRow, ErrorState, EmptyState } from '@/components/common/TableFragments'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import { useCompany } from '@/hooks/useCompany'
import { useUserProfile } from '@/hooks/useUserProfile'
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename, quizExportColumns } from '@/utils/leadExport'

// ---------------- Types / Props ----------------
interface Props { clientCompanyId: string; workspaceId: string; campaignId: string; webId: string; baseWebPath: string; web?: { Id: string; Name: string; WebType: string } }
type LeadsMode = 'contacts' | 'companies'

// ---------------- Mode Hook (path-based) ----------------
function useWebLeadsMode(baseWebPath: string): [LeadsMode, (m: LeadsMode) => void] {
    const location = useLocation()
    // Derive from path each render. Shares currentPathname with the workspace twin so a query
    // string cannot make one of them fall back to contacts while the other does not.
    const mode: LeadsMode = /\/leads\/companies$/.test(currentPathname(location)) ? 'companies' : 'contacts'
    const setMode = (next: LeadsMode) => {
        if (next === mode) return
        const target = next === 'contacts' ? `${baseWebPath}/leads` : `${baseWebPath}/leads/companies`
        try { location.route(target) } catch { if (typeof window !== 'undefined') { window.history.pushState({}, '', target); window.dispatchEvent(new PopStateEvent('popstate')) } }
    }
    return [mode, setMode]
}

// ---------------- Main Component ----------------
export const WebLeadsPanel: FunctionalComponent<Props> = ({ clientCompanyId, workspaceId, campaignId, webId, baseWebPath }) => {
    const { company, currentWorkspace } = useCompany()
    const { getWorkspaceCapabilities } = useUserProfile()
    const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
    const [data, setData] = useState<CampaignLeadAnalyticsResponseDto | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [mode, setMode] = useWebLeadsMode(baseWebPath)
    const [audienceView, setAudienceView] = useState<AudienceView>(DEFAULT_AUDIENCE_VIEW)
    const [showNonProspects, setShowNonProspects] = useState(false)
    const [selectedLead, setSelectedLead] = useState<LeadSignalTimelineDto | null>(null)

    // Live-flip the grid's enrichment cell when a lead's enrichment finishes (ORB-383).
    useEnrichmentEvent((evt) => {
        if (!evt.leadId) return
        setData(prev => prev ? { ...prev, Leads: prev.Leads.map(l => l.LeadId === evt.leadId ? { ...l, EnrichmentStatus: evt.enrichmentStatus } : l) } : prev)
    })
    const EMPTY: CampaignLeadAnalyticsResponseDto = { CampaignId: campaignId, Leads: [], Companies: [], LeadCount: 0, NonProspectCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() }

    const load = (signal?: AbortSignal) => {
        setLoading(true)
        setError(null)
        leadsApi.getWebLeads(campaignId, workspaceId, webId, signal)
            .then(r => {
                const payload = r?.data as CampaignLeadAnalyticsResponseDto | undefined
                if (payload && Array.isArray(payload.Leads)) setData(payload)
                else setData(EMPTY)
            })
            .catch(e => {
                // Don't show error if request was aborted
                if (e instanceof Error && e.name === 'AbortError') return
                setError(e?.message || 'Unknown error')
                setData(EMPTY)
            })
            .finally(() => setLoading(false))
    }
    useEffect(() => {
        const abortController = new AbortController()
        load(abortController.signal)
        return () => abortController.abort()
    }, [campaignId, workspaceId, webId])

    const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—'
    const leads = data?.Leads || []
    const companies = data?.Companies || []
    // Companies follow the same non-prospect switch as Contacts, or the two modes
    // disagree about whether ORTEC and OrbitalX are in this campaign (ORB-619).
    const visibleCompanies = filterCompaniesByAudience(companies, audienceView, showNonProspects)
    // Check if there's an Online Event web type in any lead
    const hasOnlineEvent = leads.some(l => l.WebTypes?.includes('Online Event'))
    // Semantics live in filterLeadsByAudience, shared with the campaign and workspace grids so
    // the three cannot drift apart again.
    const filteredLeads = mode === 'contacts'
        ? filterLeadsByAudience(leads, audienceView, showNonProspects)
        : leads

    // Custom comparator for attributionSource, which needs this panel's webId and so cannot live in
    // leadSorting.ts with the rest. Memoised to keep one identity for DataGrid's comparator memo.
    const getWebLeadComparator = useCallback((key: string) => {
        if (key === 'attributionSource') {
            return (a: LeadSignalTimelineDto, b: LeadSignalTimelineDto): number => {
                const aSource = a.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || ''
                const bSource = b.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || ''
                return aSource.localeCompare(bSource)
            }
        }
        return getLeadComparator(key)
    }, [webId])

    // Deselect lead if filtered out
    useEffect(() => { if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId)) setSelectedLead(null) }, [filteredLeads, selectedLead])
    // Close on ESC
    useEffect(() => { if (!selectedLead) return; const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedLead(null) }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h) }, [selectedLead])

    // Helper to update a lead in the data state
    const updateLeadInState = (leadId: string, updates: Partial<LeadSignalTimelineDto>) => {
        setData(prev => {
            if (!prev) return prev
            const nextLeads = prev.Leads.map(lead => {
                if (lead.LeadId !== leadId) return lead
                return { ...lead, ...updates }
            })
            return { ...prev, Leads: nextLeads }
        })
        setSelectedLead(curr => {
            if (!curr || curr.LeadId !== leadId) return curr
            return { ...curr, ...updates }
        })
    }

    // ---------- Contact columns for DataGrid ----------
    const contactColumns = useMemo((): ColumnConfig<LeadSignalTimelineDto>[] => {
        const cols: ColumnConfig<LeadSignalTimelineDto>[] = [
            {
                key: 'lead',
                header: 'Lead',
                sortable: true,
                required: true,
                className: 'whitespace-nowrap',
                render: (l) => {
                    const name = getLeadDisplayName(l)
                    const showEmail = shouldShowEmailLine(l, name)
                    return (
                        <div class="flex items-center gap-3">
                            <PersonAvatar firstName={l.FirstName} lastName={l.LastName} fullName={l.FullName} profilePicUrl={l.ProfilePicUrl} alt={name} />
                            <div class="flex flex-col leading-tight min-w-0">
                                <span class="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]" title={name}>{name}</span>
                                {showEmail && l.Email && <span class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={l.Email as string}>{l.Email}</span>}
                            </div>
                        </div>
                    )
                },
            },
            {
                key: 'phone',
                header: 'Phone',
                sortable: true,
                render: (l, index) => (
                    <PhoneEnrichmentCell
                        phone={l.Phone}
                        globalContactPhone={l.GlobalContactPhone}
                        phoneEnrichmentAvailableDate={l.PhoneEnrichmentAvailableDate}
                        id={l.LeadId}
                        clientCompanyId={clientCompanyId}
                        workspaceId={workspaceId}
                        campaignId={campaignId}
                        canEdit={canEdit}
                        isLastRow={index === filteredLeads.length - 1 && filteredLeads.length > 1}
                        onPhoneUpdated={(updated) => {
                            const phoneUpdate = { ...updated, Phone: updated.GlobalContactPhone || updated.Phone }
                            updateLeadInState(l.LeadId, phoneUpdate)
                        }}
                    />
                ),
            },
            {
                key: 'enrichment',
                header: 'Enrichment',
                sortable: true,
                render: (l) => (
                    <EnrichmentStatusCell
                        enrichmentStatus={l.EnrichmentStatus}
                        enrichedAt={l.EnrichedAt}
                        enrichmentAvailableDate={l.EnrichmentAvailableDate}
                        enrichmentAttemptCount={l.EnrichmentAttemptCount}
                        clientCompanyId={clientCompanyId}
                        workspaceId={workspaceId}
                        campaignId={campaignId}
                        id={l.LeadId}
                        onEnrichmentUpdated={(updated) => updateLeadInState(l.LeadId, updated)}
                    />
                ),
            },
            {
                key: 'company',
                header: 'Company',
                sortable: true,
                className: 'truncate max-w-[160px]',
                render: (l) => <span title={l.Company || '—'}>{l.Company || '—'}</span>,
            },
            {
                key: 'title',
                header: 'Title',
                sortable: true,
                className: 'truncate max-w-[180px]',
                render: (l) => <span title={l.JobTitle || '—'}>{l.JobTitle || '—'}</span>,
            },
            {
                key: 'types',
                header: 'Types',
                render: (l) => (
                    <div class="flex flex-wrap gap-1">
                        {l.WebTypes?.map(wt => {
                            const meta = getWebTypeMeta(wt as WebTypeKey | string)
                            return <span key={wt} class={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>{wt}</span>
                        })}
                    </div>
                ),
            },
            {
                key: 'attributionSource',
                header: 'Attribution Source',
                sortable: true,
                className: 'whitespace-nowrap',
                render: (l) => <>{l.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || '—'}</>,
            },
        ]

        // Dynamic column: webinar attended (only when an Online Event web type exists)
        if (hasOnlineEvent) {
            cols.push({
                key: 'webinarAttended',
                header: 'Webinar Attended',
                sortable: true,
                defaultVisible: false,
                className: 'whitespace-nowrap',
                render: (l) => <>{l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '—'}</>,
            })
        }

        cols.push(
            {
                key: 'lastConversion',
                header: 'Last Conversion',
                sortable: true,
                className: 'whitespace-nowrap',
                render: (l) => <>{l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—'}</>,
            },
            {
                key: 'signal',
                header: 'Campaign Signal',
                sortable: true,
                required: true,
                className: 'whitespace-nowrap',
                render: (l) => <SignalBars score={l.SignalScore} />,
            },
        )

        return cols
        // canEdit arrives asynchronously with the role sets, and nothing else here changes when it
        // does — without it an editor who lands before roles load keeps a read-only phone cell.
    }, [clientCompanyId, workspaceId, campaignId, webId, hasOnlineEvent, filteredLeads.length, canEdit])

    // ---------- Company columns for DataGrid ----------
    // Deps carry the scope ids: the company cell builds a workspace-scoped route from them, so an empty
    // dep array kept linking to the previous workspace when the scope changed without an unmount.
    const companyColumns = useMemo((): ColumnConfig<CompanySignalDto>[] => [
        {
            key: 'company',
            header: 'Company',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => (
                <CompanyNameCell company={c} clientCompanyId={clientCompanyId} workspaceId={workspaceId} />
            ),
        },
        {
            key: 'url',
            header: 'URL',
            className: 'truncate max-w-[180px]',
            render: (c) => {
                if (!c.CompanyWebsite) return <>{'—'}</>
                try {
                    const host = new URL(c.CompanyWebsite).hostname.replace(/^www\./, '')
                    return <a href={c.CompanyWebsite} target="_blank" rel="noopener noreferrer" class="hover:underline" title={c.CompanyWebsite}>{host}</a>
                } catch {
                    return <a href={c.CompanyWebsite} target="_blank" rel="noopener noreferrer" class="hover:underline" title={c.CompanyWebsite}>{c.CompanyWebsite}</a>
                }
            },
        },
        {
            key: 'industry',
            header: 'Industry',
            sortable: true,
            className: 'truncate max-w-[160px]',
            render: (c) => <span title={c.Industry || '—'}>{c.Industry || '—'}</span>,
        },
        {
            key: 'size',
            header: 'Size',
            sortable: true,
            render: (c) => <>{c.StaffCountRange || '—'}</>,
        },
        {
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (c) => <>{c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—'}</>,
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            render: (c) => <>{c.LeadCount}</>,
        },
        {
            key: 'signal',
            header: 'Campaign Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => <SignalBars score={c.SignalScore} />,
        },
    ], [clientCompanyId, workspaceId])

    // ---------- Export configs for DataGrid ----------
    const contactExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Web', 'leads'),
        columns: [...LEAD_EXPORT_COLUMNS, ...quizExportColumns(leads)].map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name, leads])

    const companyExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Web', 'companies'),
        columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name])

    const MetricsBar = () => (
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                <span><span class="font-semibold text-gray-900 dark:text-gray-100">Conversions:</span> {data?.LeadCount ?? '—'}</span>
                <span><span class="font-semibold text-gray-900 dark:text-gray-100">Companies:</span> {data?.CompanyCount ?? '—'}</span>
                <span class="hidden xs:inline"><span class="font-semibold text-gray-900 dark:text-gray-100">Generated:</span> {generatedRelative}</span>
                <AudienceViewToggle
                        view={audienceView}
                        onChange={setAudienceView}
                        showNonProspects={showNonProspects}
                        onToggleNonProspects={() => setShowNonProspects(v => !v)}
                        // Counts what the switch would actually reveal in the mode you're in:
                        // hidden leads in Contacts, hidden companies in Companies.
                        nonProspectCount={mode === 'contacts'
                            ? data?.NonProspectCount
                            : companies.filter(company => company.IsNonProspect).length}
                                          />
            </div>
            <div class="flex items-center gap-2">
                <LeadsModeSwitch mode={mode} onChange={m => setMode(m)} />
                <button onClick={() => load()} class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" title="Refresh">
                    <RefreshCcw class="w-4 h-4" />
                    Refresh
                </button>
            </div>
        </div>
    )

    if (loading) return (
        <div class="w-full"><MetricsBar /><table class="min-w-full text-sm"><tbody>{Array.from({ length: 3 }).map((_, i) => <LoadingRow key={i} />)}</tbody></table></div>
    )
    if (error) return (
        <div><MetricsBar /><ErrorState error={error} onRetry={() => load()} /></div>
    )

    const showLeads = mode === 'contacts'
    const showCompanies = mode === 'companies'
    if (showLeads && filteredLeads.length === 0) return (
        <div><MetricsBar /><EmptyState message={emptyStateMessage(audienceView, showNonProspects, leads.length)} /></div>
    )
    if (showCompanies && visibleCompanies.length === 0) return (
        <div><MetricsBar /><EmptyState message="No company signal yet." /></div>
    )

    return (
        <>
            <div class="w-full">
                <MetricsBar />
                {showLeads && (
                    <DataGrid<LeadSignalTimelineDto>
                        title="Contacts"
                        data={filteredLeads}
                        columns={contactColumns}
                        getRowKey={(l) => l.LeadId}
                        onRowClick={setSelectedLead}
                        getSortComparator={getWebLeadComparator}
                        scrollable
                        pagination
                        // Same as the campaign tab: the audience controls are all that narrow this,
                        // and row count alone cannot tell two of their combinations apart.
                        pageResetKey={`${audienceView}|${showNonProspects}`}
                        columnVisibilityKey="orbitalx-lead-columns-web"
                        exportConfig={contactExportConfig}
                    />
                )}
                {showCompanies && (
                    <DataGrid<CompanySignalDto>
                        title="Companies"
                        data={visibleCompanies}
                        columns={companyColumns}
                        getRowKey={(c) => c.CompanyId || c.CompanyName}
                        getSortComparator={getCompanyComparator}
                        scrollable
                        pagination
                        pageResetKey={`${audienceView}|${showNonProspects}`}
                        columnVisibilityKey="orbitalx-company-columns-web"
                        exportConfig={companyExportConfig}
                    />
                )}
            </div>
            {selectedLead && (
                <ContactPeekFlyout
                    anchorType="lead"
                    anchorId={selectedLead.LeadId}
                    clientCompanyId={clientCompanyId}
                    workspaceId={workspaceId}
                    campaignId={campaignId}
                    webId={webId}
                    onClose={() => setSelectedLead(null)}
                />
            )}
        </>
    )
}

// ---------------- Mode Switch ----------------
const LeadsModeSwitch: FunctionalComponent<{ mode: LeadsMode; onChange: (m: LeadsMode) => void }> = ({ mode, onChange }) => {
    const options: Array<{ key: LeadsMode; label: string }> = [
        { key: 'contacts', label: 'Contacts' },
        { key: 'companies', label: 'Companies' }
    ]
    return (
        <div role="radiogroup" aria-label="Leads view mode" class="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
            {options.map(o => {
                const active = o.key === mode
                return (
                    <button
                        key={o.key}
                        role="radio"
                        aria-checked={active}
                        type="button"
                        onClick={() => { if (!active) onChange(o.key) }}
                        class={`px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10 ${active ? 'bg-green-600 text-white dark:bg-green-500' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >{o.label}</button>
                )
            })}
        </div>
    )
}

export default WebLeadsPanel
